import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { SecurityConfirmDialog } from '../../components/SecurityConfirmDialog';
import { CreditManagementSection } from '../../components/superadmin/CreditManagementSection';
import { ComplimentaryAccountDialog } from '../../components/superadmin/ComplimentaryAccountDialog';
import { CustomerStripeProfile } from './CustomerStripeProfile';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, Building2, Edit, Check, X, Thermometer, ArrowLeft, 
  Users, Eye, MapPin, Plus, UserPlus, Key, Loader2, Calendar, User, Sliders, Clock,
  CheckCircle, AlertCircle, PauseCircle, Archive, Mail, XCircle, Lock, RefreshCw, CreditCard, Gift
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomerList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status');
  
  const [customers, setCustomers] = useState([]);
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(statusFilter || 'all');
  const [showArchive, setShowArchive] = useState(false);
  
  // Edit Dialog
  const [editCustomer, setEditCustomer] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Details Dialog with Users
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [customerMeasuringPoints, setCustomerMeasuringPoints] = useState([]);
  const [customerAffiliateInfo, setCustomerAffiliateInfo] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Create Customer Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [cancelCompCustomer, setCancelCompCustomer] = useState(null);
  const [cancelCompLoading, setCancelCompLoading] = useState(false);
  const [createTab, setCreateTab] = useState('quick');
  const [creating, setCreating] = useState(false);
  const [stripeSync, setStripeSync] = useState(false);
  const [stripeSyncFull, setStripeSyncFull] = useState(false);
  const [lastSyncMsg, setLastSyncMsg] = useState('');
  const [couponDlg, setCouponDlg] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [stripeProfileCustomer, setStripeProfileCustomer] = useState(null);
  const [promoCodesList, setPromoCodesList] = useState([]);
  
  // Quick Create Form (Onboarding-Flow)
  const [quickForm, setQuickForm] = useState({
    first_name: '', last_name: '', email: '', industry: '', brand: ''
  });
  
  // Industries list
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  
  // Invitation Link State
  const [invitationLink, setInvitationLink] = useState('');
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  
  // Full Create Form
  const [fullForm, setFullForm] = useState({
    company_name: '', contact_name: '', email: '', password: '',
    industry: 'tankstelle', phone: '', mobile: '', tax_number: '',
    vat_id: '', street: '', house_number: '', postal_code: '', city: '', locations: []
  });
  
  // Location management for create form
  const [newLocationName, setNewLocationName] = useState('');
  
  // Password Reset Dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  
  // Security Confirm Dialogs
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [unblockConfirmOpen, setUnblockConfirmOpen] = useState(false);
  const [passwordResetConfirmOpen, setPasswordResetConfirmOpen] = useState(false);

  // Sync active tab with URL
  useEffect(() => {
    if (statusFilter) {
      setActiveTab(statusFilter);
    } else {
      setActiveTab('all');
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchCustomers();
    fetchArchivedCustomers();
    fetchIndustries();
  }, []);

  useEffect(() => {
    let filtered = customers;
    
    // Filter nach kanonischem Kontostatus (identisch zur Dashboard-Zählung)
    const tabToAccountStatus = {
      pending: 'trialing',
      active: 'active',
      trial_expired: 'trial_expired',
      payment_issue: 'payment_issue',
      inactive: 'suspended',
    };
    if (tabToAccountStatus[activeTab]) {
      const want = tabToAccountStatus[activeTab];
      filtered = filtered.filter(c => c.account_status === want);
    } else if (activeTab === 'canceled') {
      // Gekündigte werden im Archiv geführt, nicht in der Hauptliste
      filtered = [];
    }
    // 'all' zeigt alle (Backend filtert Gekündigte/Demo bereits aus)
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.company_name?.toLowerCase().includes(term) ||
        c.contact_email?.toLowerCase().includes(term) ||
        c.customer_number?.toLowerCase().includes(term)
      );
    }
    setFilteredCustomers(filtered);
  }, [customers, searchTerm, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ status: tab });
    }
  };

  useEffect(() => {
    const fetchCity = async () => {
      if (fullForm.postal_code && fullForm.postal_code.length === 5) {
        setCityLoading(true);
        try {
          const response = await axios.get(`${API}/zip-lookup?postal_code=${fullForm.postal_code}`);
          if (response.data.city) {
            setFullForm(prev => ({ ...prev, city: response.data.city }));
          }
        } catch (error) {
          // Silently ignore zip lookup errors
        }
        finally { setCityLoading(false); }
      }
    };
    fetchCity();
  }, [fullForm.postal_code]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedCustomers = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/customers/archive`);
      setArchivedCustomers(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der archivierten Kunden', error);
    }
  };

  
  const fetchIndustries = async () => {
    setLoadingIndustries(true);
    try {
      const response = await axios.get(`${API}/superadmin/industries`);
      setIndustries(response.data.industries || []);
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.error('Fehler beim Laden der Branchen');
    } finally {
      setLoadingIndustries(false);
    }
  };

  const openCouponDlg = async (customer) => {
    // Öffne stattdessen das neue erweiterte Kundenprofil auf dem Gutschein-Tab
    setStripeProfileCustomer({ ...customer, _openTab: 'coupons' });
    if (!promoCodesList.length) {
      try {
        const r = await axios.get(`${API}/superadmin/promo-codes`);
        setPromoCodesList(r.data || []);
      } catch { /* silent */ }
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponDlg || !selectedCoupon) return;
    setApplyingCoupon(true);
    try {
      const r = await axios.post(
        `${API}/superadmin/customers/${couponDlg.id}/apply-coupon?coupon_id=${selectedCoupon}`
      );
      toast.success(r.data.message || 'Gutschein erfolgreich vergeben!', { duration: 6000 });
      setCouponDlg(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Vergeben');
    } finally { setApplyingCoupon(false); }
  };

  const handleStripeSync = async () => {    setStripeSync(true);
    try {
      const res = await axios.post(`${API}/superadmin/jobs/stripe-reconcile`);
      const { fixed = [], skipped = [], blocked = [] } = res.data;
      const msg = `${fixed.length} Status aktualisiert · ${blocked.length} gesperrt · ${skipped.length} übersprungen`;
      if (fixed.length > 0 || blocked.length > 0) {
        toast.success(`Status-Abgleich: ${msg}`, { duration: 8000 });
      } else {
        toast.info(`Status-Abgleich: ${msg}`, { duration: 6000 });
      }
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stripe-Abgleich fehlgeschlagen');
    } finally {
      setStripeSync(false);
    }
  };

  const handleFullStripeSync = async () => {
    setStripeSyncFull(true);
    setLastSyncMsg('');
    try {
      const res = await axios.post(`${API}/superadmin/jobs/sync-all-stripe-details`);
      const { synced = 0, failed = 0, total = 0 } = res.data;
      const msg = `${synced}/${total} Kunden synchronisiert${failed > 0 ? ` · ${failed} Fehler` : ''}`;
      setLastSyncMsg(msg);
      toast.success(`Komplett-Sync: ${msg}`, { duration: 8000 });
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Komplett-Sync fehlgeschlagen');
    } finally {
      setStripeSyncFull(false);
    }
  };

  const handleApprove = async (customerId) => {
    try {
      await axios.patch(`${API}/superadmin/customers/${customerId}/approve`);
      toast.success('Kunde freigeschaltet');
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler bei Freischaltung');
    }
  };

  const handleBlock = (customer) => {
    setSelectedCustomer(customer);
    setBlockConfirmOpen(true);
  };

  const executeBlock = async () => {
    try {
      await axios.patch(`${API}/superadmin/customers/${selectedCustomer.id}/block`);
      toast.success('Kunde gesperrt');
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler beim Sperren');
    }
  };

  const handleUnblock = (customer) => {
    setSelectedCustomer(customer);
    setUnblockConfirmOpen(true);
  };

  const executeUnblock = async () => {
    try {
      await axios.patch(`${API}/superadmin/customers/${selectedCustomer.id}/unblock`);
      toast.success('Kunde entsperrt');
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler beim Entsperren');
    }
  };

  const handleEdit = (customer) => {
    setEditCustomer({ ...customer });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/superadmin/customers/${editCustomer.id}`, editCustomer);
      toast.success('Kunde aktualisiert');
      setEditDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleCancelAndComp = async () => {
    if (!cancelCompCustomer) return;
    setCancelCompLoading(true);
    try {
      const res = await axios.post(`${API}/superadmin/customers/${cancelCompCustomer.id}/cancel-and-comp`);
      toast.success(res.data?.message || 'Abo storniert und auf kostenlos gestellt');
      setCancelCompCustomer(null);
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Kündigen/Umstellen');
    } finally {
      setCancelCompLoading(false);
    }
  };

  // Open Details with all users
  const openDetailsDialog = async (customer) => {
    setSelectedCustomer(customer);
    setDetailsLoading(true);
    setDetailsDialogOpen(true);
    setCustomerAffiliateInfo(null);

    // Einzeln laden statt Promise.all – ein Fehler blockiert nicht alles
    try {
      const usersRes = await axios.get(`${API}/superadmin/customers/${customer.id}/users`);
      setCustomerUsers(usersRes.data || []);
    } catch { setCustomerUsers([]); }

    try {
      const pointsRes = await axios.get(`${API}/superadmin/customers/${customer.id}/measuring-points`);
      setCustomerMeasuringPoints(pointsRes.data || []);
    } catch { setCustomerMeasuringPoints([]); }

    try {
      const detailRes = await axios.get(`${API}/superadmin/customers/${customer.id}`);
      setCustomerAffiliateInfo(detailRes.data?.affiliate_info || null);
    } catch { /* kein kritischer Fehler */ }

    setDetailsLoading(false);
  };

  // IMPERSONATION - Als User ansehen
  const handleImpersonate = async (userId, userName) => {
    // WICHTIG: Verhindere verschachtelte Impersonation
    const existingImpersonation = localStorage.getItem('impersonation');
    if (existingImpersonation) {
      toast.error('Sie sind bereits in einem Impersonation-Modus. Bitte beenden Sie diesen zuerst.');
      return;
    }

    try {
      const response = await axios.post(`${API}/superadmin/impersonate/${userId}`);
      const { access_token, impersonated_user } = response.data;
      
      if (!access_token || !impersonated_user) {
        toast.error('Ungültige Antwort vom Server');
        return;
      }
      
      // Save current superadmin token and user BEFORE switching
      const originalToken = localStorage.getItem('token');
      const originalUser = localStorage.getItem('user');
      
      // Store impersonation data with original credentials
      const impersonationData = {
        id: impersonated_user.id,
        name: impersonated_user.name,
        email: impersonated_user.email,
        role: impersonated_user.role,
        by: 'superadmin',
        originalToken: originalToken,
        originalUser: originalUser
      };
      
      localStorage.setItem('impersonation', JSON.stringify(impersonationData));
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(impersonated_user));
      
      // Update axios header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success(`Sie sehen jetzt als ${userName}`);
      
      // Navigate to target dashboard - FULL PAGE RELOAD happens automatically
      const targetUrl = impersonated_user.role === 'admin' ? '/admin' : '/employee';
      window.location.href = targetUrl;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler bei Impersonation';
      toast.error(errorMsg);
      console.error('Impersonation error:', error);
    }
  };

  const handleCreateQuick = async () => {
    if (!quickForm.first_name || !quickForm.last_name || !quickForm.email || !quickForm.industry) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/superadmin/quick-create-user`, quickForm);
      
      // Show invitation link
      setInvitationLink(response.data.invitation_link);
      setInvitationDialogOpen(true);
      
      toast.success('Kunde erfolgreich erstellt! Einladungslink wurde generiert.');
      setCreateDialogOpen(false);
      setQuickForm({ first_name: '', last_name: '', email: '', industry: '', brand: '' });
      
      // Refresh customer/user list
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };
  
  const copyInvitationLink = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(invitationLink);
        toast.success('Link kopiert!');
      } else {
        // Fallback for non-HTTPS environments (localhost)
        const textArea = document.createElement('textarea');
        textArea.value = invitationLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          textArea.remove();
          toast.success('Link kopiert!');
        } catch (err) {
          textArea.remove();
          toast.error('Link konnte nicht kopiert werden. Bitte manuell kopieren.');
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Link konnte nicht kopiert werden. Bitte manuell kopieren.');
    }
  };
  
  // Note: addLocationToQuickForm and removeLocationFromQuickForm removed as not needed for quick create

  const handleCreateFull = async () => {
    if (!fullForm.company_name || !fullForm.contact_name || !fullForm.email || !fullForm.password || !fullForm.mobile) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    if (fullForm.locations.length === 0) {
      toast.error('Bitte mindestens einen Standort hinzufügen');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/superadmin/customers/create-full`, fullForm);
      toast.success(`Kunde erstellt! Kundennummer: ${response.data.customer_number}\nStandorte: ${fullForm.locations.length}`);
      setCreateDialogOpen(false);
      setFullForm({
        company_name: '', contact_name: '', email: '', password: '',
        industry: 'tankstelle', phone: '', mobile: '', tax_number: '',
        vat_id: '', street: '', house_number: '', postal_code: '', city: '', locations: []
      });
      setNewLocationName('');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };
  
  const addLocationToFullForm = () => {
    if (!newLocationName.trim()) {
      toast.error('Standort-Name darf nicht leer sein');
      return;
    }
    setFullForm({...fullForm, locations: [...fullForm.locations, newLocationName.trim()]});
    setNewLocationName('');
  };
  
  const removeLocationFromFullForm = (index) => {
    setFullForm({...fullForm, locations: fullForm.locations.filter((_, i) => i !== index)});
  };

  const openResetPasswordDialog = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setPasswordResetConfirmOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    setResettingPassword(true);
    try {
      await axios.post(`${API}/superadmin/reset-password`, {
        user_id: resetPasswordUser.id,
        new_password: newPassword
      });
      toast.success('Passwort wurde zurückgesetzt');
      setPasswordResetConfirmOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen des Passworts');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleResendInvitation = async (customer) => {
    try {
      const response = await axios.post(`${API}/superadmin/resend-invitation/${customer.id}`);
      
      // Show success message with the new link
      toast.success(
        <div>
          <p className="font-semibold">Einladung erneut gesendet!</p>
          <p className="text-sm mt-1">Eine neue E-Mail wurde an {customer.contact_email} versendet.</p>
          {response.data.warning && (
            <p className="text-xs text-orange-600 mt-1">{response.data.warning}</p>
          )}
        </div>,
        { duration: 5000 }
      );
      
      // Optionally show the link for manual sharing
      if (response.data.invitation_link) {
        console.log('New invitation link:', response.data.invitation_link);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim erneuten Senden der Einladung');
    }
  };


  const getStatusBadge = (customer) => {
    const stripe_status = customer.stripe_status;
    const stripe_subscription_id = customer.stripe_subscription_id;
    const is_manually_blocked = customer.is_manually_blocked;
    const status = customer.status;
    const billing_status = customer.billing_status || '';

    // ── Dunning-Status (AllFlowX intern) – hat Vorrang ────────────────────
    if (billing_status === 'locked_exports') {
      return (
        <Badge className="bg-red-600 text-white flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Gesperrt (nur Exports)
        </Badge>
      );
    }
    if (billing_status === 'locked') {
      return (
        <Badge className="bg-orange-600 text-white flex items-center gap-1">
          <Lock className="h-3 w-3" />
          Konto gesperrt (Tag 5+)
        </Badge>
      );
    }
    if (billing_status === 'cancelled') {
      return (
        <Badge className="bg-red-700 text-white flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Abo gekündigt
        </Badge>
      );
    }
    if (billing_status === 'past_due') {
      const failedAt = customer.payment_failed_at ? new Date(customer.payment_failed_at) : null;
      const daysOverdue = failedAt ? Math.floor((new Date() - failedAt) / (1000 * 60 * 60 * 24)) : 0;
      return (
        <Badge className="bg-yellow-500 text-white flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Zahlung überfällig{daysOverdue > 0 ? ` (${daysOverdue}d)` : ''}
        </Badge>
      );
    }

    // Gekündigt
    if (status === 'canceled') {
      return (
        <Badge className="bg-gray-500 text-white flex items-center gap-1">
          <Archive className="h-3 w-3" />
          Gekündigt
        </Badge>
      );
    }

    // ── Echter Subscription-Status (Source of Truth aus subscriptions) ───
    // Muss VOR dem Testphase-Fallback stehen: Billing-V2/aktive Abos tragen
    // oft keine stripe_subscription_id auf dem customer-doc.
    const subscription_status = customer.subscription_status;
    if (customer.is_complimentary) {
      return (
        <Badge className="bg-emerald-600 text-white flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Kostenlos
        </Badge>
      );
    }
    if (subscription_status === 'active') {
      return (
        <Badge className="bg-green-500 text-white flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Aktiv
        </Badge>
      );
    }

    // Testphase (Pending)
    if (!stripe_subscription_id || stripe_status === 'incomplete' || status === 'pending') {
      return (
        <Badge className="bg-yellow-500 text-white flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Testphase
        </Badge>
      );
    }

    // Aktiv
    if (stripe_status === 'active' || stripe_status === 'trialing' || status === 'active') {
      return (
        <Badge className="bg-green-500 text-white flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Aktiv
        </Badge>
      );
    }

    // Zahlungsproblem (Stripe)
    if (stripe_status === 'past_due' || stripe_status === 'unpaid') {
      return (
        <Badge className="bg-orange-500 text-white flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Zahlungsproblem
        </Badge>
      );
    }

    // Inaktiv/Blocked
    if (stripe_status === 'paused' || is_manually_blocked || status === 'blocked') {
      return (
        <Badge className="bg-gray-600 text-white flex items-center gap-1">
          <PauseCircle className="h-3 w-3" />
          {status === 'blocked' ? 'Gesperrt' : 'Inaktiv'}
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        Unbekannt
      </Badge>
    );
  };

  const getTestphaseDuration = (customer) => {
    // Only show for customers in testphase
    const stripe_status = customer.stripe_status;
    const stripe_subscription_id = customer.stripe_subscription_id;
    const status = customer.status;
    
    const isTestphase = !stripe_subscription_id || stripe_status === 'incomplete' || status === 'pending';
    if (!isTestphase) return null;

    const TRIAL_PERIOD_DAYS = 30; // Standard-Testphase: 30 Tage
    const now = new Date();

    // Priorität 1: Stripe trial_end (falls vorhanden)
    if (customer.trial_end || customer.stripe_trial_end) {
      const trialEndDate = new Date(customer.trial_end || customer.stripe_trial_end);
      const daysRemaining = Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining > 0) {
        // Warnung bei 5 oder weniger Tagen
        const isUrgent = daysRemaining <= 5;
        return (
          <span className={`text-xs flex items-center gap-1 mt-1 ${isUrgent ? 'text-orange-600 font-semibold' : 'text-gray-600'}`}>
            <Clock className="h-3 w-3" />
            Noch {daysRemaining} Tag{daysRemaining !== 1 ? 'e' : ''} Testphase
          </span>
        );
      } else {
        return (
          <span className="text-xs text-red-600 flex items-center gap-1 mt-1 font-semibold">
            <X className="h-3 w-3" />
            Testphase abgelaufen
          </span>
        );
      }
    }
    
    // Priorität 2: Berechnung basierend auf created_at (30 Tage Standard)
    if (customer.created_at) {
      const createdDate = new Date(customer.created_at);
      const daysSinceCreation = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      const daysRemaining = TRIAL_PERIOD_DAYS - daysSinceCreation;
      
      if (daysRemaining > 0) {
        // Warnung bei 5 oder weniger Tagen
        const isUrgent = daysRemaining <= 5;
        return (
          <span className={`text-xs flex items-center gap-1 mt-1 ${isUrgent ? 'text-orange-600 font-semibold' : 'text-gray-600'}`}>
            <Calendar className="h-3 w-3" />
            Noch {daysRemaining} Tag{daysRemaining !== 1 ? 'e' : ''} Testphase
          </span>
        );
      } else {
        return (
          <span className="text-xs text-red-600 flex items-center gap-1 mt-1 font-semibold">
            <X className="h-3 w-3" />
            Testphase abgelaufen
          </span>
        );
      }
    }
    
    return null;
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-blue-100 text-blue-800',
      employee: 'bg-gray-100 text-gray-800',
      inhaber: 'bg-purple-100 text-purple-800',
      stationsleitung: 'bg-orange-100 text-orange-800'
    };
    const labels = { admin: 'Admin', employee: 'Mitarbeiter', inhaber: 'Inhaber', stationsleitung: 'Stationsleitung' };
    return <Badge className={styles[role] || 'bg-gray-100'}>{labels[role] || role}</Badge>;
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/superadmin/dashboard')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Kundenverwaltung</h1>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                {showArchive ? 'DSGVO-konformes Archiv gekündigter Kunden' : (
                  <>
                    {activeTab === 'pending' && 'Kunden in Testphase'}
                    {activeTab === 'trial_expired' && 'Testphase abgelaufen (ohne bezahltes Abo)'}
                    {activeTab === 'active' && 'Aktive Kunden'}
                    {activeTab === 'all' && 'Alle Kunden verwalten'}
                    {activeTab === 'payment_issue' && 'Kunden mit Zahlungsproblemen'}
                    {activeTab === 'inactive' && 'Inaktive Kunden'}
                  </>
                )}
              </p>
            </div>
          </div>
          <Button
            variant={showArchive ? "outline" : "default"}
            onClick={() => setShowArchive(!showArchive)}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            {showArchive ? (
              <>
                <Building2 className="h-4 w-4" />
                <span className="sm:inline">Aktive Kunden</span>
              </>
            ) : (
              <>
                <Archive className="h-4 w-4" />
                <span className="sm:inline">Archiv ({archivedCustomers.length})</span>
              </>
            )}
          </Button>
        </div>

        {showArchive ? (
          // Archive View - DSGVO-compliant
          <Card className="border-gray-300 bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-700">
                <Archive className="h-5 w-5" />
                Archivierte Kunden (DSGVO-konform)
              </CardTitle>
              <CardDescription>
                Diese Ansicht zeigt nur Kunden-IDs und Kündigungsdaten. Keine personenbezogenen Daten werden angezeigt.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {archivedCustomers.length > 0 ? (
                <div className="space-y-2">
                  {/* Desktop Header */}
                  <div className="hidden sm:grid grid-cols-3 gap-4 p-3 bg-gray-200 rounded font-semibold text-sm text-gray-700">
                    <div>Kundennummer</div>
                    <div>Erstellt am</div>
                    <div>Gekündigt am</div>
                  </div>
                  {archivedCustomers.map((customer) => (
                    <div key={customer.customer_id}>
                      {/* Desktop View */}
                      <div className="hidden sm:grid grid-cols-3 gap-4 p-3 bg-white rounded border border-gray-200 text-sm">
                        <div className="font-mono text-gray-700">{customer.customer_number || customer.customer_id}</div>
                        <div className="text-gray-600">
                          {customer.created_at ? new Date(customer.created_at).toLocaleDateString('de-DE') : '-'}
                        </div>
                        <div className="text-gray-600">
                          {customer.canceled_at ? new Date(customer.canceled_at).toLocaleDateString('de-DE') : '-'}
                        </div>
                      </div>
                      {/* Mobile View - Stacked */}
                      <div className="sm:hidden p-3 bg-white rounded border border-gray-200 space-y-2">
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Kundennummer</span>
                          <div className="font-mono text-sm text-gray-700">{customer.customer_number || customer.customer_id}</div>
                        </div>
                        <div className="flex justify-between gap-4">
                          <div>
                            <span className="text-xs font-semibold text-gray-500">Erstellt</span>
                            <div className="text-sm text-gray-600">
                              {customer.created_at ? new Date(customer.created_at).toLocaleDateString('de-DE') : '-'}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs font-semibold text-gray-500">Gekündigt</span>
                            <div className="text-sm text-gray-600">
                              {customer.canceled_at ? new Date(customer.canceled_at).toLocaleDateString('de-DE') : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <Archive className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-sm sm:text-base">Keine archivierten Kunden vorhanden</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
        {/* Tabs - Mobile Optimized */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="w-full overflow-x-auto pb-2">
            <TabsList className="w-full sm:w-auto inline-flex min-w-full sm:min-w-0">
              <TabsTrigger value="all" className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm">
                <Building2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Alle</span>
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Test</span>
              </TabsTrigger>
              <TabsTrigger value="trial_expired" data-testid="customer-tab-trial-expired" className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Test abgel.</span>
              </TabsTrigger>
              <TabsTrigger value="active" className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Aktiv</span>
              </TabsTrigger>
              <TabsTrigger value="payment_issue" className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm">
                <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Zahlung</span>
              </TabsTrigger>
              <TabsTrigger value="inactive" className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm">
                <PauseCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Inaktiv</span>
              </TabsTrigger>
            </TabsList>
          </div>
        </Tabs>

        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" /> Kunde anlegen
          </Button>
          <ComplimentaryAccountDialog onCreated={fetchCustomers} />
          <Button
            variant="outline"
            onClick={handleStripeSync}
            disabled={stripeSync || stripeSyncFull}
            className="w-full sm:w-auto gap-1.5 text-xs"
            title="Stripe-Abonnement-Status aktualisieren (Testphase, Gesperrt, etc.)"
            data-testid="stripe-sync-btn"
          >
            {stripeSync
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Status-Abgleich…</>
              : <><RefreshCw className="h-3.5 w-3.5" /> Status-Abgleich</>
            }
          </Button>
          <Button
            variant="outline"
            onClick={handleFullStripeSync}
            disabled={stripeSync || stripeSyncFull}
            className="w-full sm:w-auto gap-1.5 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
            title="Zahlungsmethoden, Rechnungen, Guthaben, Plan für alle Kunden synchronisieren"
            data-testid="stripe-full-sync-btn"
          >
            {stripeSyncFull
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Komplett-Sync…</>
              : <><CreditCard className="h-3.5 w-3.5" /> Stripe Komplett-Sync</>
            }
          </Button>
          {lastSyncMsg && (
            <span className="text-xs text-muted-foreground hidden sm:block">{lastSyncMsg}</span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
          </div>
        ) : filteredCustomers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">Keine Kunden gefunden</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredCustomers.map((customer) => (
              <Card key={customer.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-3 sm:gap-4">
                    {/* Customer Info Section */}
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="p-2 sm:p-3 bg-primary/10 rounded-lg flex-shrink-0">
                        <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base sm:text-lg truncate">{customer.company_name}</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{customer.contact_email}</p>
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
                          {getStatusBadge(customer)}
                          <span className="text-xs text-muted-foreground">#{customer.customer_number}</span>
                          {customer.brand ? (
                            <Badge variant="outline" className="text-xs font-semibold border-blue-200 text-blue-700 dark:text-blue-300" data-testid={`customer-brand-badge-${customer.id}`}>
                              {customer.brand}
                            </Badge>
                          ) : customer.industry === 'tankstelle' && (
                            <Badge variant="outline" className="text-xs border-amber-200 text-amber-600 dark:text-amber-400" title="Keine Marke gesetzt – Produkt-Vorschläge fehlen">
                              Keine Marke
                            </Badge>
                          )}
                          {customer.balance_euros > 0 && (
                            <Badge variant="secondary" className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 text-xs">
                              💰 {customer.balance_euros.toFixed(2)}€ Stripe-Guthaben
                            </Badge>
                          )}
                          {customer.active_coupon_info?.is_full_discount && (
                            <Badge variant="secondary" className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                              ✓ 100% Rabatt (Interner Account)
                            </Badge>
                          )}
                          {customer.active_coupon && !customer.active_coupon_info?.is_full_discount && (
                            <Badge variant="secondary" className="bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 text-xs">
                              🎟️ {customer.active_coupon_info?.percent_off
                                ? `${customer.active_coupon_info.percent_off}% Rabatt`
                                : customer.active_coupon_info?.amount_off_euros
                                  ? `${customer.active_coupon_info.amount_off_euros}€ Rabatt`
                                  : customer.active_coupon}
                            </Badge>
                          )}
                          {customer.referred_by && (
                            <Badge variant="secondary" className="bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 text-xs">
                              Geworben via {customer.referred_by}
                            </Badge>
                          )}
                        </div>
                        {getTestphaseDuration(customer)}
                      </div>
                    </div>
                    
                    {/* Action Buttons Section - Mobile Optimized */}
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                      {customer.status === 'pending' && !customer.has_password && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openDetailsDialog(customer)} className="w-full sm:w-auto text-xs sm:text-sm">
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden xs:inline">Kundenkonto öffnen</span>
                            <span className="xs:hidden">Öffnen</span>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => handleResendInvitation(customer)}
                            className="w-full sm:w-auto text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden sm:inline">Einladung erneut senden</span>
                            <span className="sm:hidden">Link neu senden</span>
                          </Button>
                        </>
                      )}
                      {customer.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={() => handleBlock(customer)} className="w-full sm:w-auto text-xs sm:text-sm">
                          <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Sperren
                        </Button>
                      )}
                      {customer.status === 'blocked' && (
                        <Button size="sm" variant="outline" onClick={() => handleUnblock(customer)} className="w-full sm:w-auto text-xs sm:text-sm">
                          <Check className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Entsperren
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openDetailsDialog(customer)} className="w-full sm:w-auto text-xs sm:text-sm">
                        <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        <span className="hidden sm:inline">Details & Impersonation</span>
                        <span className="sm:hidden">Details</span>
                      </Button>
                      {/* Vollständiges Kundenprofil – NEU */}
                      <Button
                        size="sm"
                        variant="default"
                        onClick={async () => {
                          try {
                            setStripeProfileCustomer(customer);
                            if (!promoCodesList.length) {
                              axios.get(`${API}/superadmin/promo-codes`)
                                .then(r => setPromoCodesList(r.data || []))
                                .catch(() => {});
                            }
                          } catch (err) {
                            console.error('Kundenprofil Fehler:', err);
                            toast.error('Kundenprofil konnte nicht geöffnet werden');
                          }
                        }}
                        className="w-full sm:w-auto text-xs sm:text-sm gap-1.5 bg-primary"
                        data-testid={`open-profile-btn-${customer.id}`}
                      >
                        <CreditCard className="h-3 w-3 sm:h-4 sm:w-4" />
                        Kundenprofil
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => navigate(`/superadmin/feature-flags/${customer.id}`)}
                        className="w-full sm:w-auto bg-purple-50 dark:bg-purple-950 hover:bg-purple-100 dark:hover:bg-purple-900 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 text-xs sm:text-sm"
                      >
                        <Sliders className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Module
                      </Button>
                      {/* Abo kündigen + auf Complimentary stellen – nur bei echter aktiver Stripe-Subscription */}
                      {customer.stripe_subscription_id
                        && ['active', 'trialing', 'past_due'].includes(customer.subscription_status)
                        && !customer.is_complimentary && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCancelCompCustomer(customer)}
                          data-testid={`cancel-comp-btn-${customer.id}`}
                          className="w-full sm:w-auto text-xs sm:text-sm bg-amber-50 dark:bg-amber-950 hover:bg-amber-100 dark:hover:bg-amber-900 border-amber-300 text-amber-800 dark:text-amber-300"
                          title="Stripe-Abo stornieren und dauerhaft auf kostenlos (Complimentary) stellen"
                        >
                          <Gift className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Abo kündigen + Kostenlos
                        </Button>
                      )}
                      <div className="flex gap-2 w-full sm:w-auto">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(customer)} className="flex-1 sm:flex-none">
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {/* Abo kündigen + Complimentary – Bestätigungsdialog */}
      <Dialog open={!!cancelCompCustomer} onOpenChange={(v) => { if (!v) setCancelCompCustomer(null); }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="cancel-comp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-amber-600" /> Abo kündigen + auf Kostenlos stellen
            </DialogTitle>
          </DialogHeader>
          {cancelCompCustomer && (
            <div className="space-y-3 py-1 text-sm">
              <p>Für folgenden Kunden wird die <strong>echte Stripe-Subscription sofort storniert</strong> und der Account danach <strong>dauerhaft auf kostenlos (Complimentary)</strong> gestellt:</p>
              <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
                <div><span className="text-muted-foreground">Kunde:</span> <strong>{cancelCompCustomer.company_name}</strong> ({cancelCompCustomer.customer_number || cancelCompCustomer.customer_id})</div>
                <div><span className="text-muted-foreground">Stripe-Subscription-ID:</span> <span className="font-mono text-xs">{cancelCompCustomer.stripe_subscription_id}</span></div>
                <div><span className="text-muted-foreground">Aktueller Status:</span> {cancelCompCustomer.subscription_status}</div>
              </div>
              <p className="text-red-600 font-medium">Diese Aktion ist unwiderruflich. Es erfolgt keine weitere Abrechnung; Stripe verlangt danach keine Zahlungsmethode mehr. Der Account bleibt voll nutzbar.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelCompCustomer(null)} disabled={cancelCompLoading}>Abbrechen</Button>
            <Button
              onClick={handleCancelAndComp}
              disabled={cancelCompLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="cancel-comp-confirm-btn"
            >
              {cancelCompLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Wird ausgeführt…</> : 'Kündigen + Kostenlos stellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Customer Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Neuen Kunden anlegen
            </DialogTitle>
          </DialogHeader>
          <Tabs value={createTab} onValueChange={setCreateTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="quick">Kurzanlage</TabsTrigger>
              <TabsTrigger value="full">Vollständig</TabsTrigger>
            </TabsList>
            <TabsContent value="quick" className="space-y-4 pt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  📧 Der User erhält einen Einladungslink per E-Mail (aktuell wird der Link hier angezeigt). Der Link ist 24 Stunden gültig.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vorname *</Label>
                  <Input 
                    value={quickForm.first_name} 
                    onChange={(e) => setQuickForm({...quickForm, first_name: e.target.value})} 
                    placeholder="Max" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nachname *</Label>
                  <Input 
                    value={quickForm.last_name} 
                    onChange={(e) => setQuickForm({...quickForm, last_name: e.target.value})} 
                    placeholder="Mustermann" 
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>E-Mail *</Label>
                <Input 
                  type="email" 
                  value={quickForm.email} 
                  onChange={(e) => setQuickForm({...quickForm, email: e.target.value})} 
                  placeholder="kunde@beispiel.de" 
                />
              </div>
              
              <div className="space-y-2">
                <Label>Branche *</Label>
                {loadingIndustries ? (
                  <div className="w-full h-10 rounded-md border border-input bg-gray-50 px-3 flex items-center text-gray-500">
                    Laden...
                  </div>
                ) : (
                  <select 
                    value={quickForm.industry || ''}
                    onChange={(e) => setQuickForm({...quickForm, industry: e.target.value, brand: ''})}
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                  >
                    <option value="">Branche auswählen</option>
                    {industries.map((ind) => (
                      <option key={ind.value} value={ind.value}>{ind.label}</option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-gray-500">z.B. Tankstelle, Kiosk, Einzelhandel</p>
              </div>
              
              {/* Brand-Feld – nur für Tankstellen */}
              {quickForm.industry === 'tankstelle' && (
                <div className="space-y-2">
                  <Label>Marke / Gesellschafter <span className="text-xs text-muted-foreground font-normal">(empfohlen)</span></Label>
                  <select
                    value={quickForm.brand || ''}
                    onChange={(e) => setQuickForm({...quickForm, brand: e.target.value})}
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                    data-testid="quick-create-brand-select"
                  >
                    <option value="">– Keine Angabe –</option>
                    {['JET','Shell','Aral','Esso','TotalEnergies','OMV','AVIA','Westfalen','HEM','Hoyer','BFT','Sprint','Raiffeisen','freie Tankstelle'].map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  <p className="text-xs text-amber-600">Ohne Marke fehlen Produkt-Vorschläge und Artikelnummern im System.</p>
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  ℹ️ Der Kunde wird automatisch als <strong>Admin</strong> angelegt und kann später selbst Mitarbeiter hinzufügen.
                </p>
              </div>
              
              <Button onClick={handleCreateQuick} disabled={creating || loadingIndustries || !quickForm.industry} className="w-full">
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Kunde anlegen
              </Button>
            </TabsContent>
            <TabsContent value="full" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Firmenname *</Label><Input value={fullForm.company_name} onChange={(e) => setFullForm({...fullForm, company_name: e.target.value})} /></div>
                <div className="space-y-2"><Label>Ansprechpartner *</Label><Input value={fullForm.contact_name} onChange={(e) => setFullForm({...fullForm, contact_name: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>PLZ</Label><Input value={fullForm.postal_code} onChange={(e) => setFullForm({...fullForm, postal_code: e.target.value.replace(/\D/g, '').slice(0,5)})} maxLength={5} /></div>
                <div className="space-y-2"><Label>Ort</Label><Input value={fullForm.city} onChange={(e) => setFullForm({...fullForm, city: e.target.value})} className={fullForm.city ? 'bg-green-50' : ''} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Mobil *</Label><Input value={fullForm.mobile} onChange={(e) => setFullForm({...fullForm, mobile: e.target.value})} /></div>
                <div className="space-y-2"><Label>E-Mail *</Label><Input type="email" value={fullForm.email} onChange={(e) => setFullForm({...fullForm, email: e.target.value})} /></div>
              </div>
              <div className="space-y-2">
                <Label>Passwort *</Label>
                <div className="flex gap-2">
                  <Input value={fullForm.password} onChange={(e) => setFullForm({...fullForm, password: e.target.value})} />
                  <Button type="button" variant="outline" onClick={() => setFullForm({...fullForm, password: generateRandomPassword()})}>Generieren</Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Standorte *</Label>
                <p className="text-sm text-muted-foreground">Fügen Sie die Standorte hinzu, die der Kunde verwalten wird</p>
                <div className="flex gap-2">
                  <Input 
                    value={newLocationName} 
                    onChange={(e) => setNewLocationName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLocationToFullForm())}
                    placeholder="z.B. Berlin Hauptbahnhof" 
                  />
                  <Button type="button" variant="outline" onClick={addLocationToFullForm}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {fullForm.locations.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {fullForm.locations.map((loc, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-secondary rounded-md">
                        <span className="text-sm flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {loc}
                        </span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeLocationFromFullForm(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <p className="text-sm text-muted-foreground">
                      Gesamt: {fullForm.locations.length} Standort{fullForm.locations.length !== 1 ? 'e' : ''}
                    </p>
                  </div>
                )}
              </div>
              
              <Button onClick={handleCreateFull} disabled={creating} className="w-full">
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Kunde erstellen
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>Kunde bearbeiten</DialogTitle></DialogHeader>
          {editCustomer && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2"><Label>Firmenname</Label><Input value={editCustomer.company_name || ''} onChange={(e) => setEditCustomer({...editCustomer, company_name: e.target.value})} /></div>
              <div className="space-y-2"><Label>Kontakt-E-Mail</Label><Input value={editCustomer.contact_email || ''} onChange={(e) => setEditCustomer({...editCustomer, contact_email: e.target.value})} /></div>
              {(editCustomer.industry === 'tankstelle' || !editCustomer.brand) && (
                <div className="space-y-2">
                  <Label>Marke / Gesellschafter</Label>
                  <select
                    value={editCustomer.brand || ''}
                    onChange={(e) => setEditCustomer({...editCustomer, brand: e.target.value})}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="edit-customer-brand-select"
                  >
                    <option value="">– Keine Angabe –</option>
                    {['JET','Shell','Aral','Esso','TotalEnergies','OMV','AVIA','Westfalen','HEM','Hoyer','BFT','Sprint','Raiffeisen','freie Tankstelle'].map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  {!editCustomer.brand && (
                    <p className="text-xs text-amber-600">Ohne Marke fehlen Produkt-Vorschläge und Artikelnummern.</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveEdit}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog with Impersonation - MOBILE OPTIMIZED */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-xl pr-8">
              <Building2 className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="truncate">{selectedCustomer?.company_name}</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Sehen Sie alle Benutzer und öffnen Sie deren Ansicht
            </DialogDescription>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              {/* Geworben von – Affiliate/Referral Info */}
              {customerAffiliateInfo && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm mb-2">
                  <span className="text-amber-600 font-medium shrink-0">
                    {customerAffiliateInfo.type === 'affiliate' ? 'Affiliate-Werber:' : 'Geworben von:'}
                  </span>
                  <span className="font-semibold text-amber-800 dark:text-amber-200">
                    {customerAffiliateInfo.name}
                  </span>
                  <span className="text-amber-500 text-xs">({customerAffiliateInfo.code})</span>
                  <span className="text-amber-500 text-xs truncate">{customerAffiliateInfo.email}</span>
                </div>
              )}
            <Tabs defaultValue="users" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="users">Benutzer</TabsTrigger>
                <TabsTrigger value="credits">💰 Stripe-Balance</TabsTrigger>
                <TabsTrigger value="measuring">Messpunkte</TabsTrigger>
              </TabsList>
              
              <TabsContent value="users" className="py-2 sm:py-4 space-y-4 sm:space-y-6">
              {/* All Users - MOBILE OPTIMIZED */}
              <div>
                <h3 className="text-sm sm:text-lg font-semibold flex items-center gap-2 mb-2 sm:mb-3">
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                  <span className="truncate">Alle Benutzer ({customerUsers.length})</span>
                </h3>
                {customerUsers.length === 0 ? (
                  <p className="text-xs sm:text-sm text-muted-foreground">Keine Benutzer vorhanden</p>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {customerUsers.map((user) => (
                      <div key={user.id} className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                        {/* MOBILE: Stack vertically, DESKTOP: Side by side */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          {/* User Info */}
                          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                            <div className={`p-1.5 sm:p-2 rounded-full flex-shrink-0 ${user.role === 'admin' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              <User className={`h-3 w-3 sm:h-4 sm:w-4 ${user.role === 'admin' ? 'text-blue-600' : 'text-gray-600'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm sm:text-base truncate">{user.name}</p>
                              <p className="text-xs sm:text-sm text-muted-foreground truncate">{user.email}</p>
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
                                {getRoleBadge(user.role)}
                                <Badge variant={user.is_active ? "default" : "secondary"} className="text-xs">
                                  {user.is_active ? 'Aktiv' : 'Inaktiv'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1 mt-1.5 sm:mt-2 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">Erstellt: {formatDate(user.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          {/* Action Buttons - MOBILE: Full width row, DESKTOP: Column */}
                          <div className="flex sm:flex-col gap-2 w-full sm:w-auto">
                            <Button 
                              size="sm" 
                              className="bg-orange-500 hover:bg-orange-600 flex-1 sm:flex-none text-xs sm:text-sm"
                              onClick={() => handleImpersonate(user.id, user.name)}
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              <span className="hidden xs:inline">Als {user.role === 'admin' ? 'Admin' : 'MA'} ansehen</span>
                              <span className="xs:hidden">Ansehen</span>
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1 sm:flex-none text-xs sm:text-sm"
                              onClick={() => openResetPasswordDialog(user)}
                            >
                              <Key className="h-3 w-3 mr-1" />
                              Passwort
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Measuring Points - MOBILE OPTIMIZED */}
              </TabsContent>

              <TabsContent value="credits" className="py-4">
                <CreditManagementSection 
                  customerId={selectedCustomer?.id}
                  customerName={selectedCustomer?.company_name}
                />
              </TabsContent>

              <TabsContent value="measuring" className="py-4">
              <div>
                <h3 className="text-sm sm:text-lg font-semibold flex items-center gap-2 mb-2 sm:mb-3">
                  <Thermometer className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600 flex-shrink-0" />
                  <span className="truncate">Messpunkte ({customerMeasuringPoints.length})</span>
                </h3>
                {customerMeasuringPoints.length === 0 ? (
                  <p className="text-xs sm:text-sm text-muted-foreground">Keine Messpunkte vorhanden</p>
                ) : (
                  <div className="space-y-2">
                    {customerMeasuringPoints.map((point) => (
                      <div key={point.id} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="font-medium text-sm sm:text-base truncate">{point.name}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground">Soll: {point.target_temp}°C</p>
                        </div>
                        <Badge variant={point.status === 'active' ? "default" : "secondary"} className="text-xs flex-shrink-0">
                          {point.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </TabsContent>
            </Tabs>
            </>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" /> Passwort zurücksetzen
            </DialogTitle>
            <DialogDescription>Neues Passwort für {resetPasswordUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Neues Passwort</Label>
              <div className="flex gap-2">
                <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Neues Passwort" />
                <Button type="button" variant="outline" onClick={() => setNewPassword(generateRandomPassword())}>Generieren</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleResetPassword} disabled={resettingPassword}>
              {resettingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Passwort setzen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security Confirm Dialogs */}
      <SecurityConfirmDialog
        open={blockConfirmOpen}
        onOpenChange={setBlockConfirmOpen}
        onConfirm={executeBlock}
        title="Kunde sperren"
        description={`"${selectedCustomer?.company_name}" wird gesperrt und verliert sofort den Zugriff auf alle Funktionen. Die Daten bleiben erhalten und können durch Entsperren wiederhergestellt werden.`}
        confirmPhrase="Ich verstehe die Konsequenzen"
        actionLabel="Kunde sperren"
        variant="destructive"
      />

      {/* Invitation Link Dialog */}
      <Dialog open={invitationDialogOpen} onOpenChange={setInvitationDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              User erfolgreich erstellt
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">
                Der User wurde erfolgreich angelegt. Der Einladungslink ist 24 Stunden gültig:
              </p>
            </div>

            {/* ALWAYS VISIBLE: Copyable Invitation Link */}
            <div className="bg-white border border-gray-300 rounded-lg p-4">
              <Label className="text-sm font-medium mb-2 block">Einladungslink (24h gültig):</Label>
              <div className="flex gap-2">
                <Input 
                  value={invitationLink} 
                  readOnly 
                  className="font-mono text-xs"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={copyInvitationLink}
                >
                  <Key className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Tipp: Link wurde automatisch per E-Mail versendet. Sie können ihn aber auch manuell kopieren und teilen.
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-xs text-orange-800">
                <strong>Hinweis:</strong> Der User muss über diesen Link sein Passwort setzen. 
                Danach wird er beim ersten Login aufgefordert, einen Standort anzulegen und ein Abo abzuschließen.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setInvitationDialogOpen(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SecurityConfirmDialog
        open={unblockConfirmOpen}
        onOpenChange={setUnblockConfirmOpen}
        onConfirm={executeUnblock}
        title="Kunde entsperren"
        description={`"${selectedCustomer?.company_name}" wird entsperrt und erhält wieder vollen Zugriff auf das System.`}
        confirmPhrase="Ich verstehe die Konsequenzen"
        actionLabel="Kunde entsperren"
        variant="default"
      />

      {/* Delete Customer Modal entfernt - DSGVO: Keine permanente Löschung erlaubt */}

      {/* ── Gutschein-Dialog ─────────────────────────────────────────── */}
      {couponDlg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-background rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-5">
            <div>
              <h3 className="text-lg font-bold text-foreground">Gutschein vergeben</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Für: <strong>{couponDlg.company_name}</strong>
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Gutschein auswählen</label>
              {coupons.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Stripe-Coupons gefunden...</p>
              ) : (
                <div className="space-y-2">
                  {coupons.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCoupon(c.id)}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        selectedCoupon === c.id
                          ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                          : 'border-border hover:border-violet-300'
                      }`}
                    >
                      <p className="font-semibold text-sm">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.label}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-xs text-amber-800 dark:text-amber-300">
              Der Gutschein wird direkt auf das aktive Stripe-Abo des Kunden angewendet und gilt für die nächste Rechnung.
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setCouponDlg(null)}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={!selectedCoupon || applyingCoupon}
                className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {applyingCoupon ? 'Wird vergeben...' : '🎟️ Vergeben'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Erweitertes Kundenprofil mit Stripe-Daten ─────────────────── */}
      <CustomerStripeProfile
        customer={stripeProfileCustomer}
        open={!!stripeProfileCustomer}
        onClose={() => setStripeProfileCustomer(null)}
        promoCodesList={promoCodesList}
        onImpersonate={stripeProfileCustomer ? () => {
          handleImpersonate(stripeProfileCustomer);
          setStripeProfileCustomer(null);
        } : null}
      />
    </DashboardLayout>
  );
};
